import MultiSelect from "components/common/MultiSelect";
import Switch from "components/common/Switch";
import CustomCheckbox from "components/form/CustomCheckbox";
import CustomField from "components/form/CustomField";
import DateTimePicker from "components/form/DateTimePicker";
import { formatISO } from "date-fns";
import { Form, Formik } from "formik";
import { handleSubmitForm } from "helpers/apiUtils";
import { useSmartBack } from "hooks/useSmartBack";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  getNotificationDetails,
  sendPushNotification,
  updateNotification,
} from "store/slices/notificationSlice";
import { formatOptionsForSelect } from "utils/formatOptions";
import { getNotificationValidationSchema } from "utils/validations";
import { MultiSelect as PrimeMultiSelect } from "primereact/multiselect";
import { getCustomersList } from "store/slices/customersSlice";
import { useQueryParam } from "hooks/useQueryParam";
import AddEditLoader from "components/common/detailsPage/AddEditLoader";
import PageHeader from "components/common/PageHeader";

const PushNotification = ({ mode }) => {
  const { t } = useTranslation();
  const goBack = useSmartBack();
  const dispatch = useDispatch();
  const isEdit = mode === "edit";
  const id = useQueryParam("id");

  const { isLoading, loading, notificationDetails } = useSelector(
    (state) => state.Notification,
  );
  const { customersList } = useSelector((state) => state.Customers);
  const [customerOptions, setCustomerOptions] = useState([]);

  const [initialValues, setInitialValues] = useState({
    title: {
      en: "",
      ar: "",
    },
    body: {
      en: "",
      ar: "",
    },
    type: "all",
    customers: [],
    isScheduled: false,
    scheduledTime: "",
  });

  useEffect(() => {
    dispatch(getCustomersList(""));

    if (isEdit && id) {
      dispatch(getNotificationDetails(id));
    }
  }, [isEdit, id, dispatch]);

  useEffect(() => {
    if (isEdit && notificationDetails) {
      const { title, body, type, scheduledTime, isScheduled, customers } =
        notificationDetails || {};

      const selectedIds = customers?.map((c) => c._id) || [];

      setInitialValues({
        ...initialValues,
        title,
        body,
        type,
        scheduledTime,
        isScheduled,
        customers: selectedIds,
        scheduledTime: scheduledTime ? new Date(scheduledTime) : "",
      });

      setCustomerOptions(
        customers?.map(({ _id, name, phone }) => ({
          name: `${phone} ${name || ""}`,
          value: _id,
        })) || [],
      );
    }
  }, [isEdit, notificationDetails]);

  useEffect(() => {
    if (customersList?.length) {
      setCustomerOptions((prev) => {
        const map = new Map(prev.map((o) => [o.value, o]));

        customersList.forEach(({ _id, name, phone }) => {
          map.set(_id, {
            name: `${phone} ${name || ""}`,
            value: _id,
          });
        });

        return Array.from(map.values());
      });
    }
  }, [customersList]);

  const handleSubmit = (values, { resetForm }) => {
    const toUTCISOString = (date) => date.toISOString();

    const payload = {
      ...values,
      ...(values.isScheduled &&
        values.scheduledTime && {
          scheduledTime: toUTCISOString(values.scheduledTime),
        }),
    };
    if (isEdit) {
      const { isScheduled, ...rest } = payload || {};
      const updatedPayload = {
        id,
        ...rest,
      };
      handleSubmitForm({
        dispatch,
        action: updateNotification,
        payload: updatedPayload,
        onSuccess: () => {
          resetForm();
          goBack();
        },
      });
    } else {
      handleSubmitForm({
        dispatch,
        action: sendPushNotification,
        payload: payload,
        onSuccess: () => resetForm(),
      });
    }
  };

  if (isEdit && loading)
    return <AddEditLoader title={t("Edit Notification")} rows={3} cols={3} />;

  return (
    <div className="page-content">
      <PageHeader
        isBackBtn={isEdit ? true : false}
        title={isEdit ? t("Edit Notification") : t("Notification")}
      />
      <div className="common_card">
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={getNotificationValidationSchema(t)}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <Row>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Title (English)")}
                      name="title.en"
                      placeholder={t("Enter Title (English)")}
                      enableTranslate
                      translateTo="ar"
                      targetField="title.ar"
                      required
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Title (Arabic)")}
                      name="title.ar"
                      placeholder={t("Enter Title (Arabic)")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="title.en"
                      required
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      as="textarea"
                      label={t("Body (English)")}
                      name="body.en"
                      placeholder={t("Enter Body (English)")}
                      enableTranslate
                      translateTo="ar"
                      targetField="body.ar"
                      required
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      as="textarea"
                      label={t("Body (Arabic)")}
                      name="body.ar"
                      placeholder={t("Enter Body (Arabic)")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="body.en"
                      required
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field ">
                    <label htmlFor="">{t("Send for")}</label>
                    <div className="d-flex gap-3">
                      <CustomCheckbox
                        label={t("All")}
                        checked={values?.type === "all"}
                        onChange={() => setFieldValue("type", "all")}
                        infoText={t(
                          "Sends this notification to all customers in the app.",
                        )}
                      />
                      <CustomCheckbox
                        label={t("Specific Customers")}
                        checked={values?.type === "specific_customers"}
                        onChange={() =>
                          setFieldValue("type", "specific_customers")
                        }
                        infoText={t(
                          "Sends this notification only to selected customers based on your selection.",
                        )}
                      />
                    </div>
                  </div>
                </Col>
                {values?.type === "specific_customers" && (
                  <Col lg={6}>
                    <div className="form_field">
                      <label>
                        {t("Customers")}
                        <span className="text-danger">*</span>
                      </label>
                      <PrimeMultiSelect
                        value={values?.customers || []}
                        options={customerOptions}
                        onChange={(e) => setFieldValue("customers", e.value)}
                        optionLabel="name"
                        optionValue="value"
                        placeholder={t("Select Customers")}
                        display="comma"
                        maxSelectedLabels={3}
                        filter={true}
                        onFilter={(e) => dispatch(getCustomersList(e.filter))}
                        className={`custom_multiselect`}
                      />
                      {/* <MultiSelect
                          label={t("Customers")}
                          required
                          name="customers"
                          value={values?.customers || []}
                          options={customersList?.map((item) => ({
                            name: item?.name,
                            value: item?._id,
                          }))}
                          placeholder={t("Select Customers")}
                          onChange={(val) => setFieldValue("customers", val)}
                          className={
                            errors?.customers && touched?.customers
                              ? "is-invalid"
                              : ""
                          }
                        /> */}
                    </div>
                  </Col>
                )}
                {!isEdit && (
                  <Col lg={6}>
                    <div className="form_field">
                      <Switch
                        label={t("Scheduled")}
                        minWidth={100}
                        checked={values?.isScheduled}
                        handleOnChange={() =>
                          setFieldValue(`isScheduled`, !values?.isScheduled)
                        }
                        disabled={isEdit}
                      />
                    </div>
                  </Col>
                )}
                {values?.isScheduled && (
                  <Col lg={6}>
                    <div className="form_field w-100">
                      <DateTimePicker
                        label={t("Scheduled Date & Time")}
                        required
                        value={values.scheduledTime}
                        onChange={(e) => {
                          setFieldValue("scheduledTime", e);
                        }}
                        hourFormat="12"
                        minDate={new Date()}
                        error={
                          errors?.scheduledTime && touched?.scheduledTime
                            ? errors?.scheduledTime
                            : null
                        }
                      />
                    </div>
                  </Col>
                )}

                <Col lg={12} className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>

                  <button
                    type="submit"
                    className="main_btn"
                    disabled={isLoading}
                  >
                    {t("Send")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default PushNotification;
